import React from 'react';
import { useFormikContext } from 'formik';
import ReactFlow, { ReactFlowProvider, Background, Controls, Panel } from 'reactflow';
import 'reactflow/dist/style.css';
import { Box, Text } from 'pounceV2';
import { SimpleRuleFormValues } from 'Pages/Build/Detections/Rules/SimpleRuleForm/SimpleRuleFormV3';
import { useTheme } from '@mui/material';
import GroupNodeV3 from './GroupNodeV3';
import TransitionEdgeV3 from './TransitionEdgeV3';
import { FlowChartElementTypes, ruleToNodesAndEdges } from './utils';
import { getFlowChartColors } from './constantsV3';
import StepNodeV3 from './StepNode/StepNodeV3';
import AlertNodeV3 from './AlertNodeV3';
import EditorErrorMessageV3 from './EditorErrorMessageV3';
import EditorEmptyMessageV3 from './EditorEmptyMessageV3';

const nodeTypes = {
  [FlowChartElementTypes.StepNode]: StepNodeV3,
  [FlowChartElementTypes.GroupNode]: GroupNodeV3,
  [FlowChartElementTypes.AlertNode]: AlertNodeV3,
};

const edgeTypes = {
  [FlowChartElementTypes.TransitionEdge]: TransitionEdgeV3,
};

const CorrelationRuleFlowChartBuilderV3 = () => {
  const {
    palette: { componentTokens, tokens },
  } = useTheme();
  const { values } = useFormikContext<SimpleRuleFormValues>();
  const reactFlowWrapper = React.useRef(null);
  const { simpleDetectionBody: yamlDetection, id: ruleID, createAlert } = values;
  const transitionBorderColor = getFlowChartColors(tokens).TRANSITION.BORDER;
  const {
    nodes: nodesFromRule,
    edges,
    error,
    warning,
  } = React.useMemo(
    () => ruleToNodesAndEdges(yamlDetection, ruleID, createAlert, transitionBorderColor),
    [yamlDetection, ruleID, createAlert, transitionBorderColor],
  );

  const showFullPageError = React.useMemo(() => error || !yamlDetection, [yamlDetection, error]);

  return (
    <ReactFlowProvider>
      <Box
        ref={reactFlowWrapper}
        style={{ height: '100%', overflowY: 'hidden' }}
        sx={{
          '.react-flow__attribution': {
            backgroundColor: tokens.background.basic.clear,
            fontSize: 'small',
            a: {
              ':before': {
                // @ts-ignore
                content: '"Powered by "',
              },
            },
          },
        }}
      >
        {showFullPageError ? (
          <ReactFlow
            nodes={[]}
            edges={[]}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            panOnDrag={false}
          >
            {!yamlDetection ? (
              <EditorEmptyMessageV3 />
            ) : (
              <EditorErrorMessageV3>
                {(error || '').includes('\n') ? (
                  <pre style={{ textWrap: 'inherit', wordBreak: 'break-word' }}>{error}</pre>
                ) : (
                  error
                )}
              </EditorErrorMessageV3>
            )}
            <Background />
          </ReactFlow>
        ) : (
          <ReactFlow
            nodes={nodesFromRule}
            edges={edges}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            draggable={false}
            preventScrolling={false}
            fitView
            fitViewOptions={{ padding: 1 }}
          >
            {warning ? (
              <Panel position="top-center">
                <Box
                  border="1px solid"
                  borderRadius="circle"
                  background={
                    componentTokens.buttons.colors.coreIcon.destructive.background.resting
                  }
                  borderColor={componentTokens.buttons.colors.coreIcon.destructive.stroke.resting}
                  p={2}
                >
                  <Text textAlign="center" variant="body/sm" whiteSpace="nowrap">
                    {warning}
                  </Text>
                </Box>
              </Panel>
            ) : null}
            <Background />
            <Controls showInteractive={false} fitViewOptions={{ padding: 0.6 }} />
          </ReactFlow>
        )}
      </Box>
    </ReactFlowProvider>
  );
};

export default CorrelationRuleFlowChartBuilderV3;
